import {create} from 'zustand';

/**
 * Phase 6-B: MULTI ProfitLoss Period Tabs — publish each slice's Audited map
 * so later tabs can render Last Year = prior Audited (session / display layer).
 * Also publishes per-slice AFTER_TAX (client / audited / lastYear) so BalanceSheet
 * BS-EQ-LFP can use Σ(all tabs) instead of last-writer-wins on setPLComputed.
 *
 * Rounding: each tab publishes getRoundedAuditedValue-by-fsId; Total/combine Σs
 * those maps (round-each-tab-then-sum) instead of Math.round on the merged tree.
 *
 * SINGLE / periods.length < 2: auditedBySlice + afterTaxBySlice stay unused;
 * ProfitLoss must keep the original setPLComputed / setPlRoundedAfterTax path.
 */
export type PlSliceAuditedMap = Record<number, number>;

export type PlSliceAfterTaxBridge = {
    client: number;
    audited: number;
    lastYear: number;
    /** Rounding-mode AFTER_TAX audited (optional) */
    roundedAudited?: number | null;
};

type PlMultiPeriodChainState = {
    auditedBySlice: Record<string, PlSliceAuditedMap>;
    afterTaxBySlice: Record<string, PlSliceAfterTaxBridge>;
    /** Per-tab getRoundedAuditedValue(node) keyed by fsId (no outer sumRounding) */
    roundedAuditedBySlice: Record<string, PlSliceAuditedMap>;
    setSliceAudited: (sliceKey: string, map: PlSliceAuditedMap) => void;
    setSliceAfterTax: (sliceKey: string, bridge: PlSliceAfterTaxBridge) => void;
    setSliceRoundedAudited: (sliceKey: string, map: PlSliceAuditedMap) => void;
    clear: () => void;
};

/** Slice identity for MULTI Σ — key selects the published map; YOA dedupes anniversary collisions. */
export type PlOrderedSlice = {key: string; yearOfAssessment?: string | null};

function normalizeOrderedSlices(
    ordered: Array<string | PlOrderedSlice> | null | undefined,
    fallbackKeys: string[],
): PlOrderedSlice[] {
    const raw = Array.isArray(ordered) && ordered.length > 0
        ? ordered
        : fallbackKeys;
    const out: PlOrderedSlice[] = [];
    for (const item of raw) {
        if (typeof item === 'string') {
            const key = String(item || '').trim();
            if (key) out.push({key});
            continue;
        }
        const key = String(item?.key || '').trim();
        if (!key) continue;
        out.push({key, yearOfAssessment: item?.yearOfAssessment});
    }
    return out;
}

/**
 * Ordered Σ of AFTER_TAX across MULTI PL tabs → BalanceSheet BS-EQ-LFP.
 * - client / audited / roundedAudited: Σ unique YOAs (same dedupe as buildTotalPlClientTree)
 * - lastYear: first included tab only (engagement comparative; matches FS single lastYear column)
 *   Also used by BS-EQ-AL roll-forward (LY AL + LY LFP + LY DIV).
 */
export function sumPlAfterTaxBridgeAcrossSlices(
    afterTaxBySlice: Record<string, PlSliceAfterTaxBridge>,
    orderedSlices: Array<string | PlOrderedSlice>,
): {client: number; audited: number; lastYear: number; roundedAudited: number | null} {
    let client = 0;
    let audited = 0;
    let lastYear = 0;
    let roundedSum = 0;
    let hasRounded = false;
    let sawFirst = false;
    const slices = normalizeOrderedSlices(orderedSlices, Object.keys(afterTaxBySlice || {}));
    const seenYoa = new Set<string>();
    for (const slice of slices) {
        const yoa = String(slice.yearOfAssessment || '').trim();
        if (yoa) {
            if (seenYoa.has(yoa)) continue;
            seenYoa.add(yoa);
        }
        const row = afterTaxBySlice[slice.key];
        if (!row) continue;
        client += Number(row.client) || 0;
        audited += Number(row.audited) || 0;
        if (!sawFirst) {
            lastYear = Number(row.lastYear) || 0;
            sawFirst = true;
        }
        if (row.roundedAudited != null && Number.isFinite(Number(row.roundedAudited))) {
            roundedSum += Number(row.roundedAudited) || 0;
            hasRounded = true;
        }
    }
    return {
        client,
        audited,
        lastYear,
        roundedAudited: hasRounded ? roundedSum : null,
    };
}

/**
 * Σ getRoundedAuditedValue across MULTI tabs for one fsId (Total/combine Audited Round).
 * Dedupe by YOA so anniversary-collision period tabs do not double Round vs Client Total.
 */
export function sumRoundedAuditedAcrossSlices(
    roundedAuditedBySlice: Record<string, PlSliceAuditedMap>,
    orderedSlices: Array<string | PlOrderedSlice>,
    fsId: number,
): number | null {
    if (!Number.isFinite(fsId) || fsId <= 0) return null;
    const slices = normalizeOrderedSlices(orderedSlices, Object.keys(roundedAuditedBySlice || {}));
    let sum = 0;
    let hit = false;
    const seenYoa = new Set<string>();
    for (const slice of slices) {
        const yoa = String(slice.yearOfAssessment || '').trim();
        if (yoa) {
            if (seenYoa.has(yoa)) continue;
            seenYoa.add(yoa);
        }
        const map = roundedAuditedBySlice[slice.key];
        if (!map || !Object.prototype.hasOwnProperty.call(map, fsId)) continue;
        sum += Number(map[fsId]) || 0;
        hit = true;
    }
    return hit ? sum : null;
}

export const usePlMultiPeriodChainStore = create<PlMultiPeriodChainState>((set) => ({
    auditedBySlice: {},
    afterTaxBySlice: {},
    roundedAuditedBySlice: {},
    setSliceAudited: (sliceKey, map) => {
        const key = String(sliceKey || '').trim();
        if (!key) return;
        set((s) => ({
            auditedBySlice: {...s.auditedBySlice, [key]: map},
        }));
    },
    setSliceAfterTax: (sliceKey, bridge) => {
        const key = String(sliceKey || '').trim();
        if (!key) return;
        set((s) => ({
            afterTaxBySlice: {...s.afterTaxBySlice, [key]: bridge},
        }));
    },
    setSliceRoundedAudited: (sliceKey, map) => {
        const key = String(sliceKey || '').trim();
        if (!key) return;
        set((s) => {
            const prev = s.roundedAuditedBySlice[key];
            if (prev && roundedMapsShallowEqual(prev, map)) return s;
            return {
                roundedAuditedBySlice: {...s.roundedAuditedBySlice, [key]: map},
            };
        });
    },
    clear: () => set({auditedBySlice: {}, afterTaxBySlice: {}, roundedAuditedBySlice: {}}),
}));

function roundedMapsShallowEqual(a: PlSliceAuditedMap, b: PlSliceAuditedMap): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
        const id = Number(k);
        if ((Number(a[id]) || 0) !== (Number(b[id]) || 0)) return false;
    }
    return true;
}
